import { useConstant } from '@cian/react-utils';

import { useEffect, useRef, useState } from 'react';

import { EventEmitter, type IEventEmitter } from 'shared/services/eventEmitter';
import { subscribeBannerLifecycleReporting } from 'shared/utils/subscribeBannerLifecycleReporting';
import { type TBannerEvents } from 'shared/types/events';

const TOTAL_BANNERS = 5;

export type TUseBannerCarouselParams = {
  dispatchLifecycleEvent(event: TBannerEvents): void;
  placeId: string;
};

export type TUseBannerCarouselResult = {
  allFailed: boolean;
  bannerEmitters: IEventEmitter[];
  visibleBanners: Array<{ position: number }>;
};

export function useBannerCarousel({
  dispatchLifecycleEvent,
  placeId,
}: TUseBannerCarouselParams): TUseBannerCarouselResult {
  /**
   * Число баннеров, видимых в данный момент. Начинается с 1 (только первый баннер).
   * Поведение полностью повторяет legacy-карусель (AdfoxListingCarouselBanners):
   *
   * - load первого баннера сразу открывает второй слот (если первый баннер не РСЯ).
   *   Дальше слоты открываются по показу (show_1px) предыдущего баннера — чтобы adfox
   *   не грузил всю карусель подряд без скролла.
   * - РСЯ-баннер (banner.direct) на первой позиции показывается как единственный слайд:
   *   второй слот не открывается.
   * - РСЯ-баннер на позиции ≥2 на render удаляется вместе с хвостом (карусель схлопывается
   *   до предыдущего слота) — при условии, что уже загружено ≥2 баннеров.
   * - Заглушка или ошибка на позиции N схлопывает карусель до N - 1.
   * - 0 означает, что все баннеры провалились — карусель не рендерится.
   */
  const [maxVisible, setMaxVisible] = useState(1);

  const hasEmittedLoadRef = useRef(false);
  const hasEmittedStubRef = useRef(false);
  // Число баннеров, по которым пришёл load. Нужно, чтобы повторить legacy-условие
  // удаления РСЯ на render только после загрузки минимум двух баннеров.
  const loadedCountRef = useRef(0);

  const bannerEmitters = useConstant<IEventEmitter[]>(() =>
    Array.from({ length: TOTAL_BANNERS }, () => new EventEmitter()),
  );

  const carouselEmitter = useConstant<IEventEmitter>(() => {
    const emitter = new EventEmitter();

    subscribeBannerLifecycleReporting({ dispatchLifecycleEvent, eventEmitter: emitter, placeId });

    return emitter;
  });

  useEffect(() => {
    const openNextSlot = (galleryPos: number): void => {
      setMaxVisible(prev => Math.min(Math.max(prev, galleryPos + 1), TOTAL_BANNERS));
    };

    const unsubscribes = bannerEmitters.flatMap((emitter, idx) => {
      const galleryPos = idx + 1;

      const settle = (): void => setMaxVisible(galleryPos - 1);

      return [
        emitter.subscribe('load', eventData => {
          loadedCountRef.current += 1;

          if (!hasEmittedLoadRef.current) {
            hasEmittedLoadRef.current = true;
            carouselEmitter.emit(eventData);

            // load первого баннера сразу открывает второй слот, если это не РСЯ.
            if (eventData.data.bundleName !== 'banner.direct') {
              openNextSlot(galleryPos);
            }
          }
        }),

        /**
         * Показ баннера (show_1px) открывает следующий слот. РСЯ-баннер (banner.direct)
         * показывается, но следующий слот с него не открывается — карусель останавливается.
         */
        emitter.subscribe('show_1px', eventData => {
          if (eventData.data.bundleName === 'banner.direct') {
            return;
          }

          openNextSlot(galleryPos);
        }),

        /**
         * legacy: если на позиции ≥2 (когда загружено уже минимум 2 баннера) отрендерился РСЯ —
         * перестаём грузить рекламу и убираем сам РСЯ вместе с хвостом карусели.
         */
        emitter.subscribe('render', eventData => {
          if (loadedCountRef.current < 2) {
            return;
          }

          if (eventData.data.bundleName === 'banner.direct') {
            setMaxVisible(galleryPos - 1);
          }
        }),

        emitter.subscribe('stub', settle),

        emitter.subscribe('error', settle),

        emitter.subscribe('close', () => {
          carouselEmitter.emit({ containerId: '', event: 'close' });
        }),
      ];
    });

    return (): void => unsubscribes.forEach(u => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allFailed = maxVisible === 0;

  useEffect(() => {
    if (allFailed && !hasEmittedStubRef.current) {
      hasEmittedStubRef.current = true;
      carouselEmitter.emit({ containerId: '', event: 'stub' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFailed]);

  return {
    allFailed,
    bannerEmitters,
    visibleBanners: Array.from({ length: maxVisible }, (_, i) => ({ position: i + 1 })),
  };
}
